import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

const Cart = () => {
  const { items, update, remove, subtotal } = useCart();
  const { lang, t } = useLang();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 4.9;
  const total = subtotal + shipping;

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-[800px] mx-auto px-6 py-24 text-center">
        <ShoppingBag size={48} className="mx-auto mb-6 text-[#D4AF37]" />
        <h1 className="font-serif text-4xl mb-4">{t("cart.title")}</h1>
        <p className="text-[#736B66] mb-8">{t("cart.empty")}</p>
        <Link to="/shop" className="luxury-btn">{t("cart.emptyCta")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-serif text-4xl lg:text-5xl mb-10">{t("cart.title")}</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-6 border-t border-[#EBE5DF]">
          {items.map((it) => (
            <div
              key={`${it.product_id}-${it.variant_id || "default"}`}
              className="grid grid-cols-[100px_1fr_auto] gap-6 py-6 border-b border-[#EBE5DF] items-center"
              data-testid={`cart-item-${it.product_id}`}
            >
              <div className="aspect-square bg-[#F5F2ED] overflow-hidden">
                {it.image && <img src={resolveImageUrl(it.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div>
                <h3 className="font-serif text-lg leading-tight mb-1">
                  {lang === "el" ? it.name_el : it.name_en}
                </h3>
                {it.variant_label && (
                  <p className="text-xs text-[#D4AF37] mb-1 uppercase tracking-widest">
                    {it.variant_label}
                  </p>
                )}
                <p className="text-sm text-[#736B66] mb-2">{format(it.price)} / item</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-[#EBE5DF]">
                    <button onClick={() => update(it.product_id, it.quantity - 1, it.variant_id)} className="px-2 py-1 hover:bg-[#F5F2ED]" data-testid={`decrease-${it.product_id}`}>
                      <Minus size={12} />
                    </button>
                    <span className="w-10 text-center text-sm">{it.quantity}</span>
                    <button onClick={() => update(it.product_id, it.quantity + 1, it.variant_id)} className="px-2 py-1 hover:bg-[#F5F2ED]" data-testid={`increase-${it.product_id}`}>
                      <Plus size={12} />
                    </button>
                  </div>
                  <button onClick={() => remove(it.product_id, it.variant_id)} className="text-xs text-[#736B66] hover:text-[#8B0000] flex items-center gap-1" data-testid={`remove-${it.product_id}`}>
                    <X size={14} /> {t("cart.remove")}
                  </button>
                </div>
              </div>
              <div className="text-right font-semibold">
                {format(it.price * it.quantity)}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-[#F5F2ED] p-8 h-fit lg:sticky lg:top-28" data-testid="cart-summary">
          <h3 className="font-serif text-2xl mb-6">{t("checkout.orderSummary")}</h3>
          <div className="space-y-3 pb-4 border-b border-[#EBE5DF]">
            <div className="flex justify-between text-sm">
              <span>{t("cart.subtotal")}</span>
              <span>{format(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>{t("cart.shipping")}</span>
              <span>{shipping === 0 ? t("cart.free") : format(shipping)}</span>
            </div>
          </div>
          <div className="flex justify-between text-lg font-semibold pt-4 mb-6" data-testid="cart-total">
            <span>{t("cart.total")}</span>
            <span>{format(total)}</span>
          </div>
          <button onClick={() => navigate("/checkout")} className="luxury-btn w-full" data-testid="checkout-button">
            {t("cart.checkout")}
          </button>
          <Link to="/shop" className="block text-center mt-4 text-sm text-[#736B66] hover:text-[#2C1E16]">
            ← {t("cart.continueShopping")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
